/**
 * The entry screen's rules, pure and away from the DOM.
 *
 * Same reason `sessions/rules.ts` exists: the load-bearing decisions here are
 * rulings, not styling, and a ruling is worth a test that needs no browser. Three
 * of them run through this file:
 *
 * **Blank is not zero** (CLAUDE.md rule 5). A staged line means counted; **no
 * staged line means not counted**, and a staged `0` is a real count of zero. The
 * distinction is structural on the wire — a blank never becomes a line — and this
 * module keeps it structural in the UI by deriving it from the presence of the
 * line rather than from anything typed.
 *
 * **The count is blind** (D-1, 2026-08-19). Nothing here reads or exposes BC
 * on-hand, and it could not: `CountListItem` has no such field, because
 * `CountListOut` does not either.
 *
 * **One total per item** (2026-08-15, option B), lot-tracked items included. `lot_tracked`
 * is carried for a marker and nothing else; there is no lot arithmetic on this
 * screen.
 */
import type { CountLine, CountListItem, CountSession, DecimalString } from "../api/types";

/** How an item stands in this session. Derived from staging, never from typing. */
export type CountState = "blank" | "zero" | "counted";

/**
 * Whether a decimal string is zero, **without parsing it into a number**.
 *
 * `parseFloat` would be correct for this one test and wrong as a habit — the rule
 * (§ 2.1, `types.ts`) is that a quantity string is never turned into a JS number
 * anywhere in this app, because the case that eventually matters is a count like
 * `7345.00496` that `Number` cannot hold exactly. So: strip a sign, strip one
 * decimal point, and ask whether every remaining digit is zero. `"0"`, `"0.0"`,
 * `"0.00000"` and `"-0.0"` are all zero; `"0.001"` is not.
 */
export function isZeroQuantity(value: DecimalString | null | undefined): boolean {
  if (value == null) return false;
  const digits = value.trim().replace(/^[+-]/, "").replace(".", "");
  return digits.length > 0 && /^0+$/.test(digits);
}

/** Staged lines by item number, upper-cased the way the backend stores them. */
export function stagedByItem(session: CountSession): Map<string, CountLine> {
  const staged = new Map<string, CountLine>();
  for (const line of session.lines) staged.set(line.item_no.trim().toUpperCase(), line);
  return staged;
}

/** How this item stands: the staged line is the only evidence consulted. */
export function countStateFor(item: CountListItem, staged: Map<string, CountLine>): CountState {
  const line = staged.get(item.item_no.trim().toUpperCase());
  if (!line) return "blank";
  return isZeroQuantity(line.counted_qty) ? "zero" : "counted";
}

export interface Progress {
  counted: number;
  total: number;
  /** Items on the list with no staged line — the ones still to visit. */
  uncounted: number;
}

/**
 * "37 of 112 counted".
 *
 * The denominator is the **list**, and the numerator counts explicit entries with
 * **zeros included** — a deliberate zero is counting work done, and a progress
 * figure that ignored it would tell a manager who had just counted an empty shelf
 * that they had not.
 *
 * Counted is measured against the list rather than against `session.lines`: a
 * staged line for an item that is not on the list (staging accepts any item BC
 * knows) must not push the numerator past the denominator.
 */
export function progressOf(items: CountListItem[], staged: Map<string, CountLine>): Progress {
  let counted = 0;
  for (const item of items) {
    if (staged.has(item.item_no.trim().toUpperCase())) counted += 1;
  }
  return { counted, total: items.length, uncounted: items.length - counted };
}

/** Group key for items BC holds no posting group for. Sorts last. */
export const UNSECTIONED = " unsectioned";
/** Heading for those items. Mirrors the review screen's wording. */
export const UNASSIGNED_SECTION_LABEL = "Unassigned";

/**
 * The provisional section order.
 *
 * **Mirrors `count/review.py`'s `PREFERRED_SECTION_ORDER`, and is pinned against
 * it by `tests/test_frontend_contract.py`.** Section order is decision **D-3**,
 * parked for the G3 meeting, so both copies are provisional — but they have to be
 * provisional in the *same* way: a manager who counts a list in one order and
 * reviews its subtotals in another has been handed two different documents.
 *
 * Known incomplete: `PACKAGING` is a real section at both areas sampled live and
 * is not named here, so it sorts alphabetically after the three that are. That is
 * on the G3 agenda rather than fixed by guessing.
 */
export const PREFERRED_SECTION_ORDER = ["INGREDIENTS", "PRODUCED", "RESALE"] as const;

export function sectionKey(item: CountListItem): string {
  const section = (item.section ?? "").trim();
  return section === "" ? UNSECTIONED : section;
}

export function sectionLabel(key: string): string {
  if (key === UNSECTIONED) return UNASSIGNED_SECTION_LABEL;
  return key.charAt(0).toUpperCase() + key.slice(1).toLowerCase();
}

function sectionRank(key: string): [number, string] {
  if (key === UNSECTIONED) return [2, ""];
  const upper = key.toUpperCase();
  const preferred = (PREFERRED_SECTION_ORDER as readonly string[]).indexOf(upper);
  if (preferred >= 0) return [0, String(preferred).padStart(2, "0")];
  return [1, upper];
}

// --- Vendor (Kevin's iPad feedback, item 8) ----------------------------------
//
// Deliberately a parallel set of three functions rather than a widening of the
// section ones. Sections carry a *ruling* — D-3's provisional order, pinned
// against `count/review.py` by `tests/test_frontend_contract.py` — and vendors
// carry none; folding them into one code path would put the pinned order one
// refactor away from a vendor list that has no business influencing it.

/** Group key for items BC holds no vendor for. Sorts last, like `UNSECTIONED`. */
export const UNVENDORED = " unvendored";
/** Heading and filter label for those items. */
export const UNASSIGNED_VENDOR_LABEL = "Unassigned";

/**
 * The item's vendor as BC holds it, or the sentinel.
 *
 * **Trimmed and otherwise untouched.** No case folding: the tenant really does
 * hold `LA SEGUNDA ST. PETE` and `La Segunda Retail Ybor`, so folding would merge
 * buckets on a guess, and the phase doc rules that the app displays and filters
 * the value as returned. The leading space in the sentinel is what keeps it from
 * colliding with a real name — every real key has been trimmed.
 */
export function vendorKey(item: CountListItem): string {
  const vendor = (item.vendor_name ?? "").trim();
  return vendor === "" ? UNVENDORED : vendor;
}

/** BC's own string, verbatim — unlike `sectionLabel`, which title-cases a code. */
export function vendorLabel(key: string): string {
  return key === UNVENDORED ? UNASSIGNED_VENDOR_LABEL : key;
}

/**
 * Last-resort tie-break: plain code-unit order, **not** `localeCompare`.
 *
 * It only ever runs between two keys that are the same word in different casing,
 * and `localeCompare`'s answer for that pair depends on the runtime's collation
 * (ICU puts lowercase first, a bare implementation does not). This gives one
 * answer on every device, which is what a stable heading order needs.
 */
function byCodeUnit(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

function vendorRank(key: string): [number, string] {
  // Unassigned last, everything else alphabetical. Case-insensitive *ordering*
  // only — two casings of one name stay two buckets, they just sort next to each
  // other, which is how a manager would notice the BC data problem.
  if (key === UNVENDORED) return [1, ""];
  return [0, key.toUpperCase()];
}

/**
 * What the list is grouped under. `"section"` is the default and is exactly
 * today's behaviour; item 8 is additive and changes nothing until it is chosen.
 */
export type GroupBy = "section" | "vendor";

export function groupKey(item: CountListItem, groupBy: GroupBy): string {
  return groupBy === "vendor" ? vendorKey(item) : sectionKey(item);
}

export function groupLabel(key: string, groupBy: GroupBy): string {
  return groupBy === "vendor" ? vendorLabel(key) : sectionLabel(key);
}

function groupRank(key: string, groupBy: GroupBy): [number, string] {
  return groupBy === "vendor" ? vendorRank(key) : sectionRank(key);
}

/** One heading's worth of items. Section by default, vendor if asked (item 8). */
export interface Section {
  key: string;
  label: string;
  items: CountListItem[];
  /** So a heading can say "12 of 40" without the screen recomputing it. */
  counted: number;
}

/**
 * How the item list within a section is ordered.
 *
 * `"list"` is the area's history-seeded order — the provisional D-3 order this
 * screen otherwise mirrors against the review screen's section order — and stays
 * the default so sorting is something a manager opts into, not a surprise on an
 * area they already know by heart. The other two are exactly the two Kevin asked
 * for (item 9): item number, and name A-Z.
 */
export type SortOrder = "list" | "item_no" | "name";

export interface EntryFilters {
  search: string;
  /** A section key, or "" for all. */
  section: string;
  uncountedOnly: boolean;
  /** Optional so every existing caller need not name it; `sectionsFor` defaults to `"list"`. */
  sort?: SortOrder;
  /**
   * A vendor key, or "" for all (item 8). **Composes with `section` rather than
   * replacing it** — a manager may narrow to one vendor within one section, and
   * neither control changes what the other does.
   */
  vendor?: string;
  /** Optional and defaulting to `"section"`, so today's behaviour is the default. */
  groupBy?: GroupBy;
}

export const NO_FILTERS: EntryFilters = {
  search: "",
  section: "",
  uncountedOnly: false,
  sort: "list",
  vendor: "",
  groupBy: "section",
};

/**
 * Order one section's items. `localeCompare` with `numeric: true` so `ING2` sorts
 * before `ING10` — a plain string sort would put `ING10` first, which is not how a
 * manager reads a shelf label.
 */
function sortItems(items: CountListItem[], sort: SortOrder): CountListItem[] {
  if (sort === "list") return items;
  const sorted = [...items];
  if (sort === "item_no") {
    sorted.sort((a, b) => a.item_no.localeCompare(b.item_no, undefined, { numeric: true }));
  } else {
    sorted.sort((a, b) =>
      (a.description ?? "").localeCompare(b.description ?? "", undefined, { numeric: true }),
    );
  }
  return sorted;
}

/**
 * Search matches **item number or description**, case-insensitively.
 *
 * Both halves matter on a real list: a manager who knows the shelf reaches for
 * "cuban bread", one reading off a label reaches for "PRO0001". Substring rather
 * than prefix for the same reason — "flour" should find "BREAD FLOUR 50LB".
 */
export function matchesSearch(item: CountListItem, search: string): boolean {
  const needle = search.trim().toLowerCase();
  if (!needle) return true;
  return (
    item.item_no.toLowerCase().includes(needle) ||
    (item.description ?? "").toLowerCase().includes(needle)
  );
}

/**
 * Group the list into ordered groups, applying the filters.
 *
 * Filtering happens **before** grouping so a group with no visible items
 * disappears along with its heading, rather than leaving an empty heading that
 * reads like "nothing left to count here".
 *
 * `filters.groupBy` chooses the headings — section (the default, and exactly what
 * this did before item 8) or vendor. The name stays `sectionsFor` because section
 * is still what it does unless told otherwise, and renaming it would touch every
 * call site to say nothing new.
 */
export function sectionsFor(
  items: CountListItem[],
  staged: Map<string, CountLine>,
  filters: EntryFilters = NO_FILTERS,
): Section[] {
  const groups = new Map<string, CountListItem[]>();
  const groupBy = filters.groupBy ?? "section";

  for (const item of items) {
    // Both filters are applied whichever way the list is grouped: they are
    // narrowings of the list, and only the *headings* depend on `groupBy`.
    if (filters.section && sectionKey(item) !== filters.section) continue;
    if (filters.vendor && vendorKey(item) !== filters.vendor) continue;
    if (!matchesSearch(item, filters.search)) continue;
    if (filters.uncountedOnly && countStateFor(item, staged) !== "blank") continue;
    const key = groupKey(item, groupBy);
    const bucket = groups.get(key);
    if (bucket) bucket.push(item);
    else groups.set(key, [item]);
  }

  return [...groups.entries()]
    .sort((a, b) => {
      const [ra, sa] = groupRank(a[0], groupBy);
      const [rb, sb] = groupRank(b[0], groupBy);
      // The raw key breaks the last tie so the order never depends on which item
      // happened to come first on the list: `Bakemark` and `BAKEMARK` rank equal
      // under the case-insensitive comparison and are still two real buckets.
      return ra - rb || sa.localeCompare(sb) || byCodeUnit(a[0], b[0]);
    })
    .map(([key, sectionItems]) => ({
      key,
      label: groupLabel(key, groupBy),
      // Item order within a section is the count list's own order — the area's
      // history-seeded order the manager has walked before — unless a sort was
      // asked for (item 9).
      items: sortItems(sectionItems, filters.sort ?? "list"),
      counted: sectionItems.filter((i) => countStateFor(i, staged) !== "blank").length,
    }));
}

/** Every section on the whole list — the section-jump menu's options. */
export function allSections(items: CountListItem[]): { key: string; label: string }[] {
  const keys = new Set(items.map(sectionKey));
  return [...keys]
    .sort((a, b) => {
      const [ra, sa] = sectionRank(a);
      const [rb, sb] = sectionRank(b);
      return ra - rb || sa.localeCompare(sb) || byCodeUnit(a, b);
    })
    .map((key) => ({ key, label: sectionLabel(key) }));
}

/**
 * Every vendor on the whole list — the vendor filter's options (item 8).
 *
 * Built from the **whole** list rather than from what is currently visible, the
 * same as `allSections`: a menu whose options disappeared as you used it would
 * make a mistyped search look like a vendor that no longer exists. "Unassigned"
 * is offered whenever any item has no vendor, which is the common case.
 */
export function allVendors(items: CountListItem[]): { key: string; label: string }[] {
  const keys = new Set(items.map(vendorKey));
  return [...keys]
    .sort((a, b) => {
      const [ra, sa] = vendorRank(a);
      const [rb, sb] = vendorRank(b);
      return ra - rb || sa.localeCompare(sb) || byCodeUnit(a, b);
    })
    .map((key) => ({ key, label: vendorLabel(key) }));
}

// --- What the field holds, and what happens when it is left ------------------

/** Per-item sync state. "Sync is not a mystery" (§ 3, S3). */
export type SyncState = "clean" | "pending" | "synced" | "refused";

/**
 * A quantity, as typed: digits and at most one decimal point.
 *
 * Strict about what it accepts and deliberately **not** normalising what it passes
 * on — `"0007"` and `"7.50"` go to the server exactly as typed, because the server
 * holds Decimal and the string is already exact. Negative is rejected here as well
 * as by the staging table: a count is not negative, and a stray minus on a touch
 * keyboard is likelier a typo than an intention.
 */
export function isQuantityText(text: string): boolean {
  const trimmed = text.trim();
  return trimmed !== "" && trimmed !== "." && /^\d*\.?\d*$/.test(trimmed);
}

/**
 * What leaving the field should do with what is in it.
 *
 * The rule this encodes is a ruling: **clearing is only ever S4's "Clear this
 * count", never a side effect of emptying a field.** An emptied box over an item
 * that *has* a staged count therefore does not send `counted_qty: null` — that
 * would make blurring destructive, and rule 5's distinction is far too important
 * to hand to an accidental gesture on a touch screen. It is refused, the staged
 * value goes back, and the manager is told where the clear action lives.
 */
export type BlurOutcome =
  | { kind: "sync"; value: string }
  | { kind: "noop" }
  | { kind: "clear-refused"; restore: string }
  | { kind: "invalid"; reason: string };

export function blurOutcome(
  typed: string,
  staged: Pick<CountLine, "counted_qty"> | undefined,
): BlurOutcome {
  const text = typed.trim();

  if (text === "") {
    if (!staged) return { kind: "noop" };
    return { kind: "clear-refused", restore: staged.counted_qty };
  }

  if (!isQuantityText(text)) {
    return {
      kind: "invalid",
      reason: text.includes("-")
        ? "A counted quantity cannot be negative. Business Central refuses it too."
        : "Enter digits, and at most one decimal point.",
    };
  }

  // Unchanged means unchanged — no request for a field somebody tapped into and
  // back out of. The comparison is on the exact strings, so re-typing `7.50` over
  // a staged `7.50` is a no-op while `7.5` over `7.50` is not: those are the same
  // number, and the server will answer `unchanged`, which is the right and cheap
  // outcome for a judgement this layer should not be making.
  if (staged && staged.counted_qty.trim() === text) return { kind: "noop" };

  return { kind: "sync", value: text };
}

// --- Folding a local draft over staging (D4) ---------------------------------

/**
 * What an item's count *is*, given what staging holds and what this device is
 * holding unsent.
 *
 * **The local draft wins**, which is § 4.4's last-write-wins seen from one row: the
 * manager holding the iPad just counted this shelf, so their number is the one the
 * screen shows even before staging has it. What the draft overwrote is reported
 * separately — the row shows the truth, the reconciliation view shows the history.
 *
 * The three-way distinction matters and is why this returns a source:
 *
 * - **a draft of `null`** — cleared on this device, not yet sent. Renders blank.
 * - **no draft, a staged line** — what BC holds.
 * - **neither** — never counted.
 *
 * A draft of `null` and no draft at all look identical on the row (both blank) and
 * are completely different facts: one is a pending instruction to delete a staging
 * line, the other is an item nobody has touched. Collapsing them is how a clear
 * gets lost.
 */
export interface ResolvedEntry {
  qty: string | null;
  source: "draft" | "staging" | "none";
  state: CountState;
}

export function resolveEntry(
  stagedQty: string | null | undefined,
  draft: { counted_qty: string | null } | undefined,
): ResolvedEntry {
  if (draft) {
    const qty = draft.counted_qty;
    return {
      qty,
      source: "draft",
      state: qty === null ? "blank" : isZeroQuantity(qty) ? "zero" : "counted",
    };
  }
  if (stagedQty !== null && stagedQty !== undefined) {
    return {
      qty: stagedQty,
      source: "staging",
      state: isZeroQuantity(stagedQty) ? "zero" : "counted",
    };
  }
  return { qty: null, source: "none", state: "blank" };
}

/**
 * Progress, counting a local draft as counted.
 *
 * A manager who has just entered twenty items offline has counted twenty items,
 * and a progress figure that only counted what had reached BC would tell them
 * otherwise while they were standing in the cooler that prevented it.
 */
export function progressWithDrafts(
  items: CountListItem[],
  staged: Map<string, CountLine>,
  drafts: Map<string, { counted_qty: string | null }>,
): Progress {
  let counted = 0;
  for (const item of items) {
    const key = item.item_no.trim().toUpperCase();
    const resolved = resolveEntry(staged.get(key)?.counted_qty, drafts.get(key));
    if (resolved.state !== "blank") counted += 1;
  }
  return { counted, total: items.length, uncounted: items.length - counted };
}

/** `sectionsFor`, with drafts folded in so "uncounted only" respects them. */
export function sectionsWithDrafts(
  items: CountListItem[],
  staged: Map<string, CountLine>,
  drafts: Map<string, { counted_qty: string | null }>,
  filters: EntryFilters = NO_FILTERS,
): Section[] {
  const countedSet = new Map<string, CountLine>();
  for (const item of items) {
    const key = item.item_no.trim().toUpperCase();
    const resolved = resolveEntry(staged.get(key)?.counted_qty, drafts.get(key));
    if (resolved.state !== "blank") {
      // A synthetic line, only so the existing section/filter code can ask the
      // one question it asks: is this item counted? The quantity it carries is the
      // resolved one, so a section's own progress counts drafts too.
      countedSet.set(key, {
        line_no: 0,
        item_no: key,
        unit_of_measure_code: null,
        counted_qty: resolved.qty ?? "0",
        entered_by: null,
        entered_at: null,
        lots: [],
      });
    }
  }
  return sectionsFor(items, countedSet, filters);
}
